// Chat hook using REST API SDK with WebSocket real-time updates (matching mobile implementation)
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  getChatChats,
  getChatChatsByChatIdMessages,
  postChatChatsByChatIdMessages,
  postChatChats,
  postChatChatsByChatIdRead,
} from '../services/api/familypal';
import type { Chat, Message } from '../services/api/familypal';
import { useAuth } from '../context';
import { ChatWebSocket, chatEventEmitter, ChatEvents } from '../services/ChatWebSocket';

// Import config to ensure SDK is configured for cookie auth BEFORE any API calls
import '../services/api/familypal/config';

export interface ChatMention {
  user_id: string;
  name?: string;
  offset: number;
  length: number;
}

export interface ChatMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string;
  text?: string;
  mentions?: ChatMention[];
  attachments?: any[];
  reply_to?: string;
  data?: any;
  status?: string;
  created_at: string;
  modified_at?: string;
}

export interface ChatThread {
  id: string;
  family_id: string;
  name?: string;
  type: 'family' | 'direct' | 'group';
  participants?: { user_id: string; chat_id: string }[];
  last_message?: any;
  unread_count?: number;
  data?: any;
  status?: string;
  tags?: any;
  created_at: string;
  modified_at?: string;
}

export interface CreateChatOptions {
  type?: 'family' | 'direct' | 'group';
  name?: string;
  participantIds?: string[];
  familyId?: string;
}

export interface UseChatResult {
  threads: ChatThread[];
  threadsLoading: boolean;
  threadsError: any;
  refetchThreads: () => void;

  messages: ChatMessage[];
  messagesLoading: boolean;
  messagesError: any;
  loadMoreMessages: () => void;
  loadingMoreMessages: boolean;
  hasMoreMessages: boolean;

  sendMessage: (chatId: string, content: string, attachmentIds?: string[], mentions?: ChatMention[], replyTo?: string) => Promise<ChatMessage | null>;
  sending: boolean;

  selectThread: (chatId: string | null) => void;
  selectedThreadId: string | null;

  createChat: (options?: CreateChatOptions) => Promise<ChatThread | null>;
  createFamilyChat: () => Promise<ChatThread | null>;
}

// Map API chat type to frontend type
function chatTypeFromApi(apiType: string): 'family' | 'direct' | 'group' {
  switch (apiType) {
    case 'family':
    case 'CHAT_TYPE_FAMILY':
    case '1':
      return 'family';
    case 'direct':
    case 'CHAT_TYPE_DIRECT':
    case '2':
      return 'direct';
    default:
      return 'group';
  }
}

// Convert API last_message to frontend format (matching mobile implementation)
function apiLastMessageToFrontend(msg: any, participants?: any[]): any {
  // Return undefined for null, undefined, or empty objects (no actual message)
  if (!msg || (typeof msg === 'object' && !msg.id && !msg.text && !msg.created_at)) {
    return undefined;
  }

  const senderId = msg.sender_id || msg.senderId || '';
  let senderName = msg.sender_name || msg.senderName || '';

  // Try to resolve sender name from participants if not provided
  if (!senderName && senderId && participants) {
    const sender = participants.find(
      (p: any) => (p.user_id || p.userId) === senderId
    );
    if (sender) {
      senderName = sender.display_name || sender.displayName || '';
    }
  }

  return {
    id: msg.id || '',
    chat_id: msg.chat_id || msg.chatId || '',
    sender_id: senderId,
    sender_name: senderName,
    text: msg.text || '',
    attachments: msg.attachments || [],
    created_at: msg.created_at || msg.createdAt || new Date().toISOString(),
    timestamp: msg.created_at ? new Date(msg.created_at).getTime() :
               msg.createdAt ? new Date(msg.createdAt).getTime() :
               msg.timestamp || Date.now(),
  };
}

// Convert API chat to thread
function apiChatToThread(chat: Chat): ChatThread {
  const rawLastMessage = (chat as any).last_message || (chat as any).lastMessage;

  return {
    id: chat.id || '',
    family_id: chat.family_id || '',
    name: chat.name,
    type: chatTypeFromApi(chat.type || 'group'),
    participants: (chat.participants || []).map((p) => ({
      user_id: p.user_id || (p as any).userId || '',
      chat_id: chat.id || '',
    })),
    last_message: apiLastMessageToFrontend(rawLastMessage, chat.participants),
    unread_count: chat.unread_count || 0,
    created_at: chat.created_at || new Date().toISOString(),
    modified_at: chat.modified_at,
  };
}

// Convert API message to chat message
function apiMessageToChat(msg: Message): ChatMessage {
  const senderId = msg.sender_id || (msg as any).senderId || (msg as any).user_id || (msg as any).userId || '';
  return {
    id: msg.id || '',
    chat_id: msg.chat_id || '',
    sender_id: senderId,
    content: msg.text || '',
    text: msg.text,
    mentions: msg.mentions?.map((m) => ({
      user_id: m.user_id,
      name: m.name,
      offset: m.offset,
      length: m.length,
    })),
    reply_to: (msg as any).reply_to || undefined,
    attachments: (msg as any).attachments || undefined,
    data: undefined,
    status: 'sent',
    created_at: msg.created_at || new Date().toISOString(),
    modified_at: msg.modified_at,
  };
}

export function useChat(familyId?: string): UseChatResult {
  const { user, session } = useAuth();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // Threads state
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState<any>(null);

  // Messages state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<any>(null);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const hasMoreRef = useRef(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  // Track if WebSocket is connected
  const wsConnectedRef = useRef(false);

  // Fetch threads from REST API
  const fetchThreads = useCallback(async () => {
    setThreadsLoading(true);
    setThreadsError(null);

    try {
      // Always fetch all chats the user participates in (no family filter).
      // Chats are participant-based and may not have a family_id.
      const result = await getChatChats({
        query: {},
      } as any);

      if (result.error) {
        console.error('Chat API error:', result.error);
        throw result.error;
      }

      const chatList = (result.data as any)?.chats || [];
      setThreads(chatList.map(apiChatToThread));
    } catch (err) {
      console.error('Failed to fetch chats:', err);
      setThreadsError(err);
    } finally {
      setThreadsLoading(false);
    }
  }, [familyId]);

  // Mark chat as read (needed for backend to broadcast READ_RECEIPT for multi-device sync)
  const markAsRead = useCallback(
    async (chatId: string, messageId: string) => {
      if (!user?.id || !chatId || !messageId) return;
      try {
        await postChatChatsByChatIdRead({
          path: { chat_id: chatId },
          body: {
            message_id: messageId,
            user_id: user.id,
          },
        } as any);
      } catch (err) {
        console.error('Failed to mark chat as read:', err);
      }
    },
    [user?.id]
  );

  // Fetch messages from REST API
  const fetchMessages = useCallback(async (chatId: string) => {
    if (!chatId) return;

    setMessagesLoading(true);
    setMessagesError(null);

    try {
      // Use 'as any' to match mobile SDK usage pattern
      const result = await getChatChatsByChatIdMessages({
        path: { chat_id: chatId },
        query: { limit: 50 },
      } as any);

      if (result.error) {
        throw result.error;
      }

      const data = result.data as any;
      const messageList = data?.messages || [];
      const more = !!data?.has_more;
      hasMoreRef.current = more;
      setHasMoreMessages(more);
      // Reverse to show oldest first (so newest is last)
      const sortedMessages = messageList.map(apiMessageToChat).reverse();
      setMessages(sortedMessages);

      // Mark as read with the latest message ID
      if (sortedMessages.length > 0) {
        const latestMsg = sortedMessages[sortedMessages.length - 1];
        markAsRead(chatId, latestMsg.id);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setMessagesError(err);
    } finally {
      setMessagesLoading(false);
    }
  }, [markAsRead]);

  // Load older messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (loadingMoreMessages || !hasMoreRef.current || !selectedThreadId || messages.length === 0) return;

    // Oldest message is the first in the array (sorted oldest→newest)
    const oldestMessage = messages[0];
    setLoadingMoreMessages(true);

    try {
      const result = await getChatChatsByChatIdMessages({
        path: { chat_id: selectedThreadId },
        query: { limit: 50, before_id: oldestMessage.id },
      } as any);

      if (result.error) throw result.error;

      const data = result.data as any;
      const messageList = data?.messages || [];
      const more = !!data?.has_more;
      hasMoreRef.current = more;
      setHasMoreMessages(more);

      // Reverse to oldest-first and prepend
      const olderMessages = messageList.map(apiMessageToChat).reverse();
      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newMsgs = olderMessages.filter((m: ChatMessage) => !existingIds.has(m.id));
        return [...newMsgs, ...prev];
      });
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [selectedThreadId, loadingMoreMessages, messages]);

  // Initial fetch of threads
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Fetch messages when thread changes
  useEffect(() => {
    if (selectedThreadId) {
      fetchMessages(selectedThreadId);
    } else {
      setMessages([]);
    }
  }, [selectedThreadId, fetchMessages]);

  // WebSocket connection for real-time updates (matching mobile implementation)
  useEffect(() => {
    if (!user?.id || !session?.id) return;

    // Set user ID and session ID for WebSocket authentication
    // Note: Web uses cookie-based auth. The session.id is the Ory session identifier
    // (not an auth token), which the backend can verify with Ory.
    ChatWebSocket.setUserId(user.id);
    ChatWebSocket.setSessionId(session.id);
    ChatWebSocket.connect(familyId).catch((err) => {
      console.error('Failed to connect to chat WebSocket:', err);
    });

    // Handle connection state
    const unsubConnected = chatEventEmitter.on(ChatEvents.CONNECTED, () => {
      console.log('useChat: WebSocket connected');
      wsConnectedRef.current = true;
    });

    const unsubDisconnected = chatEventEmitter.on(ChatEvents.DISCONNECTED, () => {
      console.log('useChat: WebSocket disconnected');
      wsConnectedRef.current = false;
    });

    return () => {
      unsubConnected();
      unsubDisconnected();
      ChatWebSocket.disconnect();
    };
  }, [familyId, user?.id, session?.id]);

  // Track subscribed chat IDs to avoid re-subscribing
  const subscribedChatIdsRef = useRef<Set<string>>(new Set());

  // Subscribe to chat IDs when threads change
  useEffect(() => {
    if (threads.length === 0) return;

    const currentChatIds = threads.map((t) => t.id);
    const newChatIds = currentChatIds.filter((id) => !subscribedChatIdsRef.current.has(id));

    if (newChatIds.length > 0) {
      console.log('useChat: Subscribing to new chat IDs:', newChatIds);
      ChatWebSocket.subscribe(newChatIds);
      newChatIds.forEach((id) => subscribedChatIdsRef.current.add(id));
    }

    // Cleanup: unsubscribe from chats that are no longer in the list
    const currentIdSet = new Set(currentChatIds);
    const toUnsubscribe = Array.from(subscribedChatIdsRef.current).filter((id) => !currentIdSet.has(id));
    if (toUnsubscribe.length > 0) {
      console.log('useChat: Unsubscribing from removed chat IDs:', toUnsubscribe);
      ChatWebSocket.unsubscribe(toUnsubscribe);
      toUnsubscribe.forEach((id) => subscribedChatIdsRef.current.delete(id));
    }
  }, [threads]);

  // Handle real-time message events
  useEffect(() => {
    // New message received
    const unsubNewMessage = chatEventEmitter.on(ChatEvents.MESSAGE_RECEIVED, (data: any) => {
      console.log('useChat: MESSAGE_RECEIVED', data);
      const chatId = data?.chat_id || data?.chatId;

      const newMsg: ChatMessage = {
        id: data.id || '',
        chat_id: chatId,
        sender_id: data.sender_id || data.senderId || data.user_id || '',
        content: data.text || '',
        text: data.text || '',
        reply_to: data.reply_to || data.replyTo || undefined,
        data: data.data,
        status: 'sent',
        created_at: data.created_at || data.createdAt || new Date().toISOString(),
        modified_at: data.modified_at || data.modifiedAt,
      };

      // If this message is for the selected thread, add it and mark as read
      if (chatId && chatId === selectedThreadId) {
        setMessages((prev) => {
          // Check if message already exists by ID (avoid duplicates)
          if (newMsg.id && prev.some((m) => m.id === newMsg.id)) {
            console.log('useChat: Skipping duplicate message by ID:', newMsg.id);
            return prev;
          }
          // Also check by content + sender + approximate time for messages without ID
          // This catches duplicates when optimistic add races with WebSocket
          const isDuplicate = prev.some((m) =>
            m.sender_id === newMsg.sender_id &&
            m.text === newMsg.text &&
            Math.abs(new Date(m.created_at).getTime() - new Date(newMsg.created_at).getTime()) < 5000
          );
          if (isDuplicate) {
            console.log('useChat: Skipping duplicate message by content/time');
            return prev;
          }
          return [...prev, newMsg];
        });

        // Mark as read since user is viewing this chat
        if (newMsg.id) {
          markAsRead(chatId, newMsg.id);
        }
      }

      // Update threads with the new message (avoiding full refresh)
      setThreads((prev) => {
        const threadIndex = prev.findIndex((t) => t.id === chatId);
        if (threadIndex === -1) return prev;

        const updated = [...prev];
        const isViewingThisChat = chatId === selectedThreadId;
        const isFromCurrentUser = newMsg.sender_id === user?.id;

        updated[threadIndex] = {
          ...updated[threadIndex],
          last_message: {
            text: newMsg.content || newMsg.text,
            sender_id: newMsg.sender_id,
            created_at: newMsg.created_at,
            attachments: data.attachments,
          },
          // Only increment unread if not viewing the chat and not from current user
          unread_count:
            isViewingThisChat || isFromCurrentUser
              ? updated[threadIndex].unread_count || 0
              : (updated[threadIndex].unread_count || 0) + 1,
        };
        return updated;
      });
    });

    // Message updated
    const unsubMessageUpdated = chatEventEmitter.on(ChatEvents.MESSAGE_UPDATED, (data: any) => {
      console.log('useChat: MESSAGE_UPDATED', data);
      const messageId = data?.id;
      const chatId = data?.chat_id;

      if (messageId && chatId === selectedThreadId) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  content: data.text || m.content,
                  text: data.text || m.text,
                  modified_at: data.modified_at || new Date().toISOString(),
                }
              : m
          )
        );
      }
    });

    // Message deleted
    const unsubMessageDeleted = chatEventEmitter.on(ChatEvents.MESSAGE_DELETED, (data: any) => {
      console.log('useChat: MESSAGE_DELETED', data);
      const messageId = data?.messageId || data?.id;
      const threadId = data?.threadId || data?.chat_id;

      if (messageId && threadId === selectedThreadId) {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      }
    });

    // Chat created - fetch the new chat and add it inline (avoid full thread list refetch)
    const unsubChatCreated = chatEventEmitter.on(ChatEvents.CHAT_CREATED, (data: any) => {
      console.log('useChat: CHAT_CREATED', data);
      const chatId = data?.chat_id || data?.chatId || data?.id;
      if (!chatId) {
        // No ID in event — fall back to full refetch
        fetchThreads();
        return;
      }
      // If the thread is already in the list (e.g. added optimistically by createChat), skip
      setThreads((prev) => {
        if (prev.some((t) => t.id === chatId)) return prev;
        // We don't have full thread data from the event, so refetch
        fetchThreads();
        return prev;
      });
    });

    // Chat updated - update thread properties inline (avoid full refetch)
    const unsubChatUpdated = chatEventEmitter.on(ChatEvents.CHAT_UPDATED, (data: any) => {
      console.log('useChat: CHAT_UPDATED', data);
      const chatId = data?.chat_id || data?.chatId || data?.id;
      if (!chatId) return;

      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.id === chatId);
        if (idx === -1) return prev;

        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          ...(data.name !== undefined && { name: data.name }),
          ...(data.status !== undefined && { status: data.status }),
        };
        return updated;
      });
    });

    // Read receipt - clear unread count when current user reads on another device
    const unsubReadReceipt = chatEventEmitter.on(ChatEvents.READ_RECEIPT_UPDATE, (data: any) => {
      console.log('useChat: READ_RECEIPT_UPDATE', data);
      const chatId = data?.threadId || data?.chatId;
      const userId = data?.userId;

      // Only clear unread if the read receipt is from the current user (multi-device sync)
      if (!chatId || userId !== user?.id) return;

      setThreads((prev) => {
        const threadIndex = prev.findIndex((t) => t.id === chatId);
        if (threadIndex === -1) return prev;

        const updated = [...prev];
        updated[threadIndex] = { ...updated[threadIndex], unread_count: 0 };
        return updated;
      });
    });

    return () => {
      unsubNewMessage();
      unsubMessageUpdated();
      unsubMessageDeleted();
      unsubChatCreated();
      unsubChatUpdated();
      unsubReadReceipt();
    };
  }, [selectedThreadId, fetchThreads, user?.id, markAsRead]);

  // Send message via REST API
  const sendMessage = useCallback(
    async (chatId: string, content: string, attachmentIds?: string[], mentions?: ChatMention[], replyTo?: string): Promise<ChatMessage | null> => {
      if (!user?.id || (!content.trim() && (!attachmentIds || attachmentIds.length === 0))) return null;

      setSending(true);
      try {
        // Use 'as any' to match mobile SDK usage pattern
        const result = await postChatChatsByChatIdMessages({
          path: { chat_id: chatId },
          body: {
            text: content.trim(),
            attachment_ids: attachmentIds,
            mentions: mentions && mentions.length > 0 ? mentions : undefined,
            reply_to: replyTo || undefined,
          },
        } as any);

        if (result.error) {
          throw result.error;
        }

        const newMsg = apiMessageToChat(result.data as Message);

        // Add to messages (with duplicate check - WebSocket may have already added it)
        setMessages((prev) => {
          if (newMsg.id && prev.some((m) => m.id === newMsg.id)) {
            return prev;
          }
          return [...prev, newMsg];
        });

        // Update thread's last_message inline (WebSocket will also deliver this,
        // but updating immediately avoids a visible delay in the thread list)
        setThreads((prev) => {
          const idx = prev.findIndex((t) => t.id === chatId);
          if (idx === -1) return prev;

          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            last_message: {
              text: newMsg.content || newMsg.text,
              sender_id: newMsg.sender_id,
              created_at: newMsg.created_at,
            },
          };
          return updated;
        });

        return newMsg;
      } catch (error) {
        console.error('Failed to send message:', error);
        throw error;
      } finally {
        setSending(false);
      }
    },
    [user?.id]
  );

  // Create a chat with participants (family_id is optional)
  const createChat = useCallback(async (options?: {
    type?: 'family' | 'direct' | 'group';
    name?: string;
    participantIds?: string[];
    familyId?: string;
  }): Promise<ChatThread | null> => {
    try {
      const chatFamilyId = options?.familyId ?? familyId;
      const chatType = options?.type === 'family' ? 'CHAT_TYPE_FAMILY' :
                       options?.type === 'direct' ? 'CHAT_TYPE_DIRECT' : 'CHAT_TYPE_GROUP';

      console.log('useChat: Creating chat', { type: chatType, familyId: chatFamilyId, participants: options?.participantIds });

      // Use 'as any' to match mobile SDK usage pattern
      const body: any = {
        type: chatType,
        name: options?.name || '',
        participant_ids: options?.participantIds || [],
      };

      // Only include family_id if provided
      if (chatFamilyId) {
        body.family_id = chatFamilyId;
      }

      const result = await postChatChats({ body } as any);

      console.log('useChat: postChatChats result:', result);

      if (result.error) {
        throw result.error;
      }

      const newChat = apiChatToThread(result.data as Chat);
      console.log('useChat: Created chat:', newChat);

      // Add the new thread immediately; CHAT_CREATED WebSocket event will
      // also arrive, but the duplicate will be harmless (same id).
      setThreads((prev) => {
        if (prev.some((t) => t.id === newChat.id)) return prev;
        return [newChat, ...prev];
      });

      return newChat;
    } catch (error) {
      console.error('Failed to create chat:', error);
      return null;
    }
  }, [familyId]);

  // Backwards-compatible alias for createChat with family type
  const createFamilyChat = useCallback(async (): Promise<ChatThread | null> => {
    return createChat({ type: 'family' });
  }, [createChat]);

  const selectThread = useCallback((chatId: string | null) => {
    setSelectedThreadId(chatId);
    // Clear unread count immediately when selecting a thread
    if (chatId) {
      setThreads((prev) =>
        prev.map((t) => (t.id === chatId ? { ...t, unread_count: 0 } : t))
      );
    }
  }, []);

  const refetchThreads = useCallback(() => {
    fetchThreads();
  }, [fetchThreads]);

  return {
    threads,
    threadsLoading,
    threadsError,
    refetchThreads,
    messages,
    messagesLoading,
    messagesError,
    loadMoreMessages,
    loadingMoreMessages,
    hasMoreMessages,
    sendMessage,
    sending,
    selectThread,
    selectedThreadId,
    createChat,
    createFamilyChat,
  };
}

export default useChat;
